import { getLocale, t } from '../i18n';
import { supabase } from '../supabaseClient';

export interface ReportProfile {
  id: string;
  company_id: string;
  full_name: string | null;
  role: string;
}

export interface Report {
  id: string;
  company_id: string;
  site_id: string | null;
  guard_id: string | null;
  shift_execution_id: string | null;
  shift_execution_segment_id: string | null;
  created_at: string;
  title: string | null;
  content: string | null;
  report_type: string | null;
  report_data: Record<string, unknown> | null;
  priority: string | null;
  status: string;
  status_updated_at: string | null;
  status_reason: string | null;
  assigned_to: string | null;
  is_auto_generated: boolean;
  [key: string]: unknown;
}

export interface ReportShiftExecution {
  id: string;
  planned_slot_date: string | null;
  planned_start_at: string | null;
  planned_end_at: string | null;
  status: string | null;
}

export interface ReportShiftExecutionSegment {
  id: string;
  planned_label: string | null;
  planned_segment_type: string | null;
  planned_start_at: string | null;
  planned_end_at: string | null;
}

export interface ReportMedia {
  id: string;
  storage_path: string;
  media_type: string;
  url: string | null;
}

export interface ReportComment {
  id: string;
  report_id: string;
  company_id: string;
  author_id: string;
  body: string;
  is_internal: boolean;
  created_at: string;
  profiles?: { full_name: string | null; role: string } | null;
  [key: string]: unknown;
}

export interface ReportStatusEvent {
  id: string;
  report_id: string;
  created_at: string;
  actor_id: string | null;
  profiles?: { full_name: string | null } | null;
  [key: string]: unknown;
}

export interface AdminProfile {
  id: string;
  full_name: string | null;
}

const REPORT_SELECT = `
  *,
  profiles:profiles!reports_guard_id_fkey(full_name),
  assignee:profiles!reports_assigned_to_fkey(id, full_name),
  sites:sites(name),
  shift_executions:shift_executions!reports_shift_execution_id_fkey(
    id,
    planned_slot_date,
    planned_start_at,
    planned_end_at,
    status
  ),
  shift_execution_segments:shift_execution_segments!reports_shift_execution_segment_id_fkey(
    id,
    planned_label,
    planned_segment_type,
    planned_start_at,
    planned_end_at
  )
`;

export type ReportCategory = 'incident' | 'summary';
export type ReportListSort =
  | 'created_desc'
  | 'created_asc'
  | 'updated_desc'
  | 'updated_asc';

export interface FetchReportsParams {
  companyId: string;
  page: number;
  pageSize: number;
  category: ReportCategory;
  search?: string;
  priority?: string;
  status?: string;
  reportType?: string;
  sortBy?: ReportListSort;
}

export type ReportListItem = Pick<
  Report,
  | 'id'
  | 'company_id'
  | 'site_id'
  | 'guard_id'
  | 'created_at'
  | 'title'
  | 'content'
  | 'report_type'
  | 'priority'
  | 'status'
  | 'status_updated_at'
  | 'assigned_to'
  | 'is_auto_generated'
> & {
  profiles?: { full_name: string | null } | null;
  assignee?: { id: string; full_name: string | null } | null;
  sites?: { name: string | null } | null;
};

export interface PaginatedReports {
  reports: ReportListItem[];
  totalCount: number;
  statusCounts: ReportStatusCounts;
}

export interface ReportStatusCounts {
  open: number;
  submitted: number;
  in_review: number;
  needs_more_info: number;
  escalated: number;
  closed: number;
}

interface ReportsPagePayload {
  reports?: ReportListItem[];
  totalCount?: number;
  statusCounts?: Partial<ReportStatusCounts>;
}

function formatShiftTimeRange(startAt: string | null | undefined, endAt: string | null | undefined): string | null {
  if (!startAt || !endAt) return null;
  const start = new Date(startAt);
  const end = new Date(endAt);
  return `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

// Reports now attach to execution records, so build the sidebar label from execution context instead of assignments.
export function getReportShiftLabel(report: Report): string {
  const segment = (report['shift_execution_segments'] as ReportShiftExecutionSegment | null | undefined) ?? null;
  const execution = (report['shift_executions'] as ReportShiftExecution | null | undefined) ?? null;

  if (segment?.planned_label?.trim()) {
    return segment.planned_label.trim();
  }

  const dateSource = execution?.planned_slot_date ?? execution?.planned_start_at ?? segment?.planned_start_at ?? null;
  const dateLabel = dateSource ? new Date(dateSource).toLocaleDateString(getLocale()) : null;
  const timeLabel = formatShiftTimeRange(
    segment?.planned_start_at ?? execution?.planned_start_at ?? null,
    segment?.planned_end_at ?? execution?.planned_end_at ?? null,
  );

  return [dateLabel, timeLabel].filter(Boolean).join(' ') || t('N/A');
}

export const reportService = {
  async getCurrentUserProfile(): Promise<ReportProfile> {
    const { data: { user }, error: authErr } = await supabase.auth.getUser();
    if (authErr || !user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('profiles')
      .select('id, company_id, full_name, role')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data as ReportProfile;
  },

  async fetchCompanyName(companyId: string): Promise<string> {
    const { data, error } = await supabase
      .from('companies')
      .select('name')
      .eq('id', companyId)
      .single();
    if (error || !data) return '';
    return (data as { name: string }).name ?? '';
  },

  async fetchReports(params: FetchReportsParams): Promise<PaginatedReports> {
    const {
      companyId,
      page,
      pageSize,
      category,
      search = '',
      priority = '',
      status = '',
      reportType = '',
      sortBy = 'created_desc',
    } = params;
    const { data, error } = await supabase.rpc('get_reports_page', {
      p_company_id: companyId,
      p_category: category,
      p_search: search.trim(),
      p_priority: priority || null,
      p_status: status || null,
      p_report_type: reportType || null,
      p_sort: sortBy,
      p_offset: page * pageSize,
      p_limit: pageSize,
    });
    if (error) throw error;
    const payload = (data ?? {}) as unknown as ReportsPagePayload;
    const counts = payload.statusCounts ?? {};
    return {
      reports: payload.reports ?? [],
      totalCount: Number(payload.totalCount ?? 0),
      statusCounts: {
        open: Number(counts.open ?? 0),
        submitted: Number(counts.submitted ?? 0),
        in_review: Number(counts.in_review ?? 0),
        needs_more_info: Number(counts.needs_more_info ?? 0),
        escalated: Number(counts.escalated ?? 0),
        closed: Number(counts.closed ?? 0),
      },
    };
  },

  async fetchReportById(reportId: string): Promise<Report> {
    const { data, error } = await supabase
      .from('reports')
      .select(REPORT_SELECT)
      .eq('id', reportId)
      .single();
    if (error) throw error;
    return data as Report;
  },

  async fetchAdmins(companyId: string): Promise<AdminProfile[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name')
      .eq('company_id', companyId)
      .in('role', ['admin', 'manager'])
      .order('full_name', { ascending: true });
    if (error) throw error;
    return (data ?? []) as AdminProfile[];
  },

  async assignReport(reportId: string, assigneeId: string | null) {
    const { data, error } = await supabase
      .from('reports')
      .update({ assigned_to: assigneeId ?? null })
      .eq('id', reportId)
      .select('assigned_to, assignee:profiles!reports_assigned_to_fkey(id, full_name)')
      .single();
    if (error) throw error;
    return data;
  },

  async fetchReportMedia(reportId: string): Promise<ReportMedia[]> {
    const { data, error } = await supabase
      .from('reports_media')
      .select('id, storage_path, media_type')
      .eq('report_id', reportId);
    if (error) throw error;

    const mediaWithUrls = await Promise.all(
      ((data ?? []) as Array<{ id: string; storage_path: string; media_type: string }>).map(async (item) => {
        const { data: urlData, error: urlError } = await supabase.storage
          .from('report-media')
          .createSignedUrl(item.storage_path, 600);
        if (urlError) return { ...item, url: null };
        return { ...item, url: urlData.signedUrl };
      }),
    );
    return mediaWithUrls.filter((m) => m.url !== null) as ReportMedia[];
  },

  async updateReportStatus(reportId: string, status: string, reason?: string | null): Promise<Report> {
    const { data, error } = await supabase
      .from('reports')
      .update({
        status,
        status_reason: reason ?? null,
      })
      .eq('id', reportId)
      .select()
      .single();
    if (error) throw error;
    return data as Report;
  },

  async fetchComments(reportId: string): Promise<ReportComment[]> {
    const { data, error } = await supabase
      .from('report_comments')
      .select('*, profiles:author_id(full_name, role)')
      .eq('report_id', reportId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as ReportComment[];
  },

  async addComment(
    reportId: string,
    companyId: string,
    authorId: string,
    body: string,
    isInternal = false,
  ): Promise<ReportComment> {
    const { data, error } = await supabase
      .from('report_comments')
      .insert({
        report_id: reportId,
        company_id: companyId,
        author_id: authorId,
        body,
        is_internal: isInternal,
      })
      .select('*, profiles:author_id(full_name, role)')
      .single();
    if (error) throw error;
    return data as ReportComment;
  },

  async deleteComment(commentId: string): Promise<void> {
    const { error } = await supabase
      .from('report_comments')
      .delete()
      .eq('id', commentId);
    if (error) throw error;
  },

  async fetchStatusEvents(reportId: string): Promise<ReportStatusEvent[]> {
    const { data, error } = await supabase
      .from('report_status_events')
      .select('*, profiles:actor_id(full_name)')
      .eq('report_id', reportId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as ReportStatusEvent[];
  },
};
